// Grenades and throwing knives in flight: one pooled equipment model per projectile id, placed at the
// (already interpolated) snapshot position and tumbling along its velocity.

import * as THREE from "three";
import { createEquipmentModel } from "./models/equipment";
import type { ProjectileRenderer } from "./types";
import type { ProjectileSnapData } from "../wasm";

/** Grenades are drawn a bit larger than life so they read at distance. */
const GRENADE_SCALE = 1.5;
const KNIFE_SCALE = 1.25;
/** Below this speed (m/s) a projectile is resting: it stops spinning. */
const REST_SPEED = 0.8;
/** Longest stretch a projectile is moved past its newest snapshot (s), like the players' extrapolation. */
const MAX_EXTRAPOLATE_S = 0.1;

const NEG_Z = new THREE.Vector3(0, 0, -1);
const UP = new THREE.Vector3(0, 1, 0);
const AXIS_X = new THREE.Vector3(1, 0, 0);
const _align = new THREE.Quaternion();
const _spin = new THREE.Quaternion();
const _dir = new THREE.Vector3();

interface Entry {
  id: number;
  kind: string;
  object: THREE.Group;
  axis: THREE.Vector3;
  vel: THREE.Vector3;
  spin: number;
  speed: number;
  /** Seconds the model was moved past its snapshot position. */
  ahead: number;
  seen: boolean;
}

/** Turns the model along its velocity and applies the tumble. */
function orient(e: Entry): void {
  _dir.copy(e.vel).multiplyScalar(1 / e.speed);
  if (e.kind === "throwing_knife") {
    // Blade (-Z) along the flight direction; the spin runs end over end around the local X axis.
    _align.setFromUnitVectors(NEG_Z, _dir);
    _spin.setFromAxisAngle(AXIS_X, e.spin);
    e.object.quaternion.copy(_align).multiply(_spin);
  } else {
    e.axis.crossVectors(_dir, UP);
    if (e.axis.lengthSq() < 1e-6) e.axis.set(1, 0, 0);
    e.axis.normalize();
    e.object.quaternion.setFromAxisAngle(e.axis, e.spin);
  }
}

export class ProjectileManager implements ProjectileRenderer {
  private readonly entries = new Map<number, Entry>();
  private readonly scene: THREE.Scene;
  private readonly failed = new Set<string>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  get count(): number {
    return this.entries.size;
  }

  /**
   * Takes a snapshot's projectiles. `ageSec` is how long ago the snapshot happened on the shown
   * timeline: the models are placed where the projectiles are by now and keep moving in `update`
   * until the next snapshot corrects them.
   */
  sync(list: ProjectileSnapData[], ageSec = 0): void {
    for (const e of this.entries.values()) e.seen = false;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      let e = this.entries.get(p.id);
      if (e === undefined || e.kind !== p.kind) {
        if (e !== undefined) this.destroy(e);
        e = this.create(p);
        if (e === undefined) continue;
      }
      e.seen = true;
      e.vel.set(p.vel.x, p.vel.y, p.vel.z);
      e.speed = e.vel.length();
      e.ahead = Math.min(ageSec, MAX_EXTRAPOLATE_S);
      e.object.position.set(p.pos.x, p.pos.y, p.pos.z).addScaledVector(e.vel, e.ahead);
      if (e.speed > REST_SPEED) orient(e);
    }
    for (const [id, e] of this.entries) {
      if (!e.seen) {
        this.destroy(e);
        this.entries.delete(id);
      }
    }
  }

  /** Every frame: moving projectiles advance along their velocity (snapshots come at the tick rate, frames faster) and tumble. */
  update(dt: number): void {
    for (const e of this.entries.values()) {
      if (e.speed <= REST_SPEED) continue;
      e.spin += dt * (e.kind === "throwing_knife" ? 22 : 6 + 0.35 * e.speed);
      // Past the newest snapshot only a short stretch is guessed.
      const step = Math.min(dt, MAX_EXTRAPOLATE_S - e.ahead);
      if (step > 0) {
        e.ahead += step;
        e.object.position.addScaledVector(e.vel, step);
      }
      orient(e);
    }
  }

  clear(): void {
    for (const e of this.entries.values()) this.destroy(e);
    this.entries.clear();
  }

  private create(p: ProjectileSnapData): Entry | undefined {
    if (this.failed.has(p.kind)) return undefined;
    let object: THREE.Group;
    try {
      object = createEquipmentModel(p.kind);
    } catch (err) {
      this.failed.add(p.kind);
      console.warn(`projectiles: no model for "${p.kind}"`, err);
      return undefined;
    }
    object.scale.setScalar(p.kind === "throwing_knife" ? KNIFE_SCALE : GRENADE_SCALE);
    object.traverse((o) => {
      if (o instanceof THREE.Mesh) o.castShadow = true;
    });
    this.scene.add(object);
    const e: Entry = {
      id: p.id,
      kind: p.kind,
      object,
      axis: new THREE.Vector3(1, 0, 0),
      vel: new THREE.Vector3(),
      spin: 0,
      speed: 0,
      ahead: 0,
      seen: true,
    };
    this.entries.set(p.id, e);
    return e;
  }

  private destroy(e: Entry): void {
    // Geometry and materials are shared through the equipment cache: only detach.
    e.object.removeFromParent();
  }
}
